import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import InvoiceBilling from './InvoiceBilling.jsx';
import PaymentPlansManager from './PaymentPlansManager.jsx';
import Reports from './Reports.jsx';
import FinancialReports from './FinancialReports.jsx';
import './BillingFinance.css';

// One "Billing & Finance" hub that replaces the old separate Billing / Payment
// Plans / Reports nav items. Each tab renders its existing component in
// `embedded` mode (own page title + duplicate money tiles suppressed).
// Tabs are gated by role so coaches keep view-only invoice access while the
// admin-only panels (Plans, Overview, Accounting) stay hidden from them.
const TABS = [
  { key: 'overview', label: 'Overview', icon: '📊', roles: ['admin', 'superadmin', 'staff'] },
  { key: 'invoices', label: 'Invoices', icon: '🧾', roles: ['admin', 'coach', 'superadmin', 'staff', 'parent'] },
  { key: 'plans', label: 'Plans & Recurring', icon: '💳', roles: ['admin'] },
  { key: 'accounting', label: 'Accounting', icon: '💰', roles: ['admin'] },
];

function BillingFinance({ initialTab = 'overview' }) {
  const { user } = useAuth();
  const role = user?.role;
  const [params, setParams] = useSearchParams();

  const visibleTabs = TABS.filter(t => t.roles.includes(role));
  const requested = params.get('tab') || initialTab;
  const active = visibleTabs.some(t => t.key === requested)
    ? requested
    : (visibleTabs[0]?.key || 'invoices');

  const setTab = (key) => setParams(key === 'overview' ? {} : { tab: key }, { replace: true });

  return (
    <div className="bf-wrap">
      <div className="bf-head">
        <h1>💵 Billing &amp; Finance</h1>
      </div>

      {visibleTabs.length > 1 && (
        <div className="bf-tabs" role="tablist">
          {visibleTabs.map(t => (
            <button
              key={t.key}
              role="tab"
              aria-selected={active === t.key}
              className={`bf-tab ${active === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span className="bf-tab-ico">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="bf-panel">
        {active === 'overview' && <Reports embedded />}
        {active === 'invoices' && <InvoiceBilling embedded />}
        {active === 'plans' && <PaymentPlansManager embedded />}
        {active === 'accounting' && <FinancialReports embedded />}
      </div>
    </div>
  );
}

export default BillingFinance;
